import React, { Component } from "react";
import { Container, Card, CardHeader, CardBody, Collapse } from "reactstrap";
import { RouterProps, Styled } from "../../../../toolkit/decorators/Artifact";
import { ProcesoDispatcher } from "../../../containers/comisionesMembresia/proceso/Proceso.dispatcher";
import { ProcesoStore } from "../../../containers/comisionesMembresia/proceso/Proceso.store";
import s from "./styles.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckDouble,
  faChevronCircleDown,
  faChevronCircleUp,
  faExclamationTriangle,
  faSpinner,
  faCalculator,
  faChartBar,
  faUsersCog,
  faChartLine,
  faFileExcel,
  faUndo
} from "@fortawesome/free-solid-svg-icons";
import {
  EstadoProcesoCierre,
  EstadoProcesoLabels,
  mayorQue,
  mayorIgualQue,
  menorQue
} from "../../../../types/EstadoProcesoCierre";
import ConfirmModal from "../../common/confirmModal/confirmModal";
import {
  downloadReportePrecalculo,
  downloadReporteAlcances,
  downloadReporteComisiones
} from "../../../actions/comisionMembresia";
import { successNotification } from "../../../../utils/notifications";

interface Props extends ProcesoDispatcher, ProcesoStore, RouterProps {}

interface State {
  openStep: number | null;
  loading: boolean;
  downloading: boolean;
  periodo: string;
  pollingInterval: any;
  confirmModal: {
    isOpen: boolean;
    title: string;
    message: string;
    action: (() => Promise<void>) | null;
  };
}

@Styled(s)
export default class ProcesoComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      openStep: null,
      loading: false,
      downloading: false,
      periodo: "",
      pollingInterval: null,
      confirmModal: {
        isOpen: false,
        title: "",
        message: "",
        action: null
      }
    };
  }

  async componentDidMount() {
    // Calcular el periodo: día 1 del mes anterior a la fecha actual
    const now = new Date();
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    const year = previousMonth.getFullYear();
    const month = String(previousMonth.getMonth() + 1).padStart(2, "0");
    const periodo = `${year}-${month}-01`;

    this.setState({ periodo });

    // Cargar el estado del proceso para el periodo calculado
    const preview = await this.props.getEstadoProceso(periodo);

    if (preview.fulfilled) {
      const estadoProceso = preview.estadoProceso;

      // Determinar qué paso mostrar según el estado del proceso
      let stepToOpen = 1; // Por defecto, paso 1

      if (!estadoProceso || !estadoProceso.estadoMembresia) {
        // Si no hay estado, mostrar paso 1 (Definición de Metas)
        stepToOpen = 1;
      } else {
        // Determinar el paso según el EstadoProcesoCierre
        switch (estadoProceso.estadoMembresia) {
          case EstadoProcesoCierre.MEMBRESIA_DEFINICION_METAS:
          case EstadoProcesoCierre.MEMBRESIA_CONFIGURACION_PLANTILLA:
            stepToOpen = 2; // Definición de Metas
            break;

          case EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_PLANTILLA:
          case EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES:
            stepToOpen = 3; // Configuración de Plantilla de Asesores
            break;

          case EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES:
            stepToOpen = 4; // Cálculo de Alcances Porcentuales
            break;

          case EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL:
            stepToOpen = 5; // Precálculo de Reglas de Pago
            break;

          case EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL:
            stepToOpen = 6; // Generación de Comisiones del Periodo
            break;

          case EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL:
          case EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL:
          case EstadoProcesoCierre.PROCESO_COMPLETADO:
            stepToOpen = 7; // Último paso (no sumamos más)
            break;

          default:
            stepToOpen = 1;
            break;
        }
      }

      // Abrir el paso determinado
      this.toggleStep(stepToOpen, true);
    }

    // Iniciar polling cada 10 segundos
    this.startPolling();
  }

  componentWillUnmount() {
    // Limpiar el intervalo al desmontar el componente
    this.stopPolling();
  }

  startPolling = () => {
    // Evitar crear múltiples intervalos
    if (this.state.pollingInterval) {
      return;
    }

    const interval = setInterval(() => {
      if (this.state.periodo) {
        this.props.getEstadoProceso(this.state.periodo);
      }
    }, 10000); // 10 segundos

    this.setState({ pollingInterval: interval });
  };

  stopPolling = () => {
    if (this.state.pollingInterval) {
      clearInterval(this.state.pollingInterval);
      this.setState({ pollingInterval: null });
    }
  };

  openConfirmModal = (
    title: string,
    message: string,
    action: () => Promise<void>
  ) => {
    this.setState({
      confirmModal: {
        isOpen: true,
        title,
        message,
        action
      }
    });
  };

  closeConfirmModal = () => {
    this.setState({
      confirmModal: {
        isOpen: false,
        title: "",
        message: "",
        action: null
      }
    });
  };

  handleConfirmAction = async () => {
    const { action } = this.state.confirmModal;
    if (action) {
      await action();
    }
    this.closeConfirmModal();
  };

  componentDidUpdate() {
    const {
      postingCalcularAlcances,
      postingConfirmarAlcances,
      postingPrecalculo,
      postingCalcularComisiones,
      estadoProceso,
      deletingCalcularAlcances,
      deletingConfirmarAlcances,
      deletingPrecalculo,
      deletingCalcularComisiones
    } = this.props;

    // Verificar si hay procesos en ejecución (desde el estado del backend)
    const isExecuting =
      estadoProceso &&
      (estadoProceso.calculoAlcancesEnEjecucion ||
        estadoProceso.calculoComisionesEnEjecucion);

    // Verificar si hay peticiones en curso
    const isPosting =
      postingCalcularAlcances ||
      postingConfirmarAlcances ||
      postingPrecalculo ||
      postingCalcularComisiones;

    const isReverting =
      deletingCalcularAlcances ||
      deletingConfirmarAlcances ||
      deletingPrecalculo ||
      deletingCalcularComisiones;

    const isLoading = isPosting || isExecuting || isReverting;

    if (isLoading !== this.state.loading) {
      this.setState({ loading: isLoading });
    }
  }

  formatPeriodo = (periodo: string): string => {
    if (!periodo) {
      return "";
    }

    const meses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre"
    ];

    const [year, month] = periodo.split("-");
    const monthIndex = parseInt(month, 10) - 1;

    return `${meses[monthIndex]} ${year}`;
  };

  isActivityAllowed = (
    actividad: EstadoProcesoCierre
  ): { allowed: boolean; reason?: string } => {
    const { estadoProceso } = this.props;
    const estadoActual = estadoProceso && estadoProceso.estadoMembresia;

    switch (actividad) {
      case EstadoProcesoCierre.MEMBRESIA_DEFINICION_METAS: {
        return mayorQue(
          estadoActual,
          EstadoProcesoCierre.MEMBRESIA_DEFINICION_METAS
        )
          ? {
              allowed: false,
              reason: "No puede definir las metas con una plantilla guardada"
            }
          : { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_CONFIGURACION_PLANTILLA: {
        return mayorQue(
          estadoActual,
          EstadoProcesoCierre.MEMBRESIA_CONFIGURACION_PLANTILLA
        )
          ? {
              allowed: false,
              reason:
                "No puede configurar la plantilla después de calcular los alcances"
            }
          : { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_PLANTILLA: {
        if (
          menorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_CONFIGURACION_PLANTILLA
          )
        ) {
          return {
            allowed: false,
            reason: "No puede confirmar la plantilla sin configurarla primero"
          };
        }
        if (
          mayorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_PLANTILLA
          )
        ) {
          return {
            allowed: false,
            reason:
              "No puede confirmar la plantilla después de calcular los alcances"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES: {
        if (
          menorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_PLANTILLA
          )
        ) {
          return {
            allowed: false,
            reason: "Debe confirmar la plantilla antes de calcular los alcances"
          };
        }
        if (
          mayorQue(estadoActual, EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES)
        ) {
          return {
            allowed: false,
            reason: "Los alcances ya fueron confirmados, no puede recalcularlos"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES: {
        if (
          menorQue(estadoActual, EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES)
        ) {
          return {
            allowed: false,
            reason: "Debe calcular los alcances antes de confirmarlos"
          };
        }
        if (
          mayorQue(estadoActual, EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES)
        ) {
          return {
            allowed: false,
            reason:
              "Los alcances ya fueron confirmados y el proceso ha avanzado"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL: {
        if (
          menorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES
          )
        ) {
          return {
            allowed: false,
            reason: "Debe confirmar los alcances antes del precálculo mensual"
          };
        }
        if (
          mayorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL
          )
        ) {
          return {
            allowed: false,
            reason: "El precálculo mensual ya fue completado"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL: {
        if (
          menorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL
          )
        ) {
          return {
            allowed: false,
            reason:
              "Debe ejecutar el precálculo mensual antes del cálculo mensual"
          };
        }
        if (
          mayorQue(estadoActual, EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL)
        ) {
          return {
            allowed: false,
            reason: "El cálculo mensual ya fue completado"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL: {
        if (
          menorQue(estadoActual, EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL)
        ) {
          return {
            allowed: false,
            reason:
              "Debe completar el cálculo mensual antes del precálculo postquincenal"
          };
        }
        if (
          mayorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL
          )
        ) {
          return {
            allowed: false,
            reason: "El precálculo postquincenal ya fue completado"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL: {
        if (
          menorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL
          )
        ) {
          return {
            allowed: false,
            reason:
              "Debe ejecutar el precálculo postquincenal antes del cálculo"
          };
        }
        if (
          mayorQue(
            estadoActual,
            EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL
          )
        ) {
          return {
            allowed: false,
            reason: "El cálculo postquincenal ya fue completado"
          };
        }
        return { allowed: true };
      }
      case EstadoProcesoCierre.PROCESO_COMPLETADO: {
        if (
          !estadoActual ||
          (estadoActual !==
            EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL &&
            estadoActual !== EstadoProcesoCierre.PROCESO_COMPLETADO)
        ) {
          return {
            allowed: false,
            reason: "Debe completar el cálculo antes de realizar esta acción"
          };
        }
        return { allowed: true };
      }
      default:
        return {
          allowed: false,
          reason: "Actividad no permitida para el Proceso de Membresías"
        };
    }
  };

  toggleStep = (step: number, ignoreLoading: boolean = false) => {
    if (this.state.loading && !ignoreLoading) {
      return;
    }
    this.setState({
      openStep: this.state.openStep === step ? null : step
    });
  };

  renderStepHeader = (
    stepNumber: number,
    title: string,
    isCompleted: boolean = false
  ) => {
    const { openStep, loading } = this.state;
    const isOpen = openStep === stepNumber;

    return (
      <CardHeader
        onClick={() => this.toggleStep(stepNumber)}
        className={`${s.procesoStepHeader} ${isOpen ? s.active : ""} ${
          isCompleted ? s.completed : ""
        } ${loading ? s.disabled : ""}`}
        style={{
          cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.6 : 1
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <span className={s.stepNumber}>{stepNumber}</span>
            <span className={s.stepTitle}>
              {title} {isCompleted && <>&#10003;</>}
            </span>
          </div>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={isOpen ? faChevronCircleUp : faChevronCircleDown}
              color="#6c757d"
            />
          </div>
        </div>
      </CardHeader>
    );
  };

  render() {
    const { openStep } = this.state;
    const estadoMembresia = this.props.estadoProceso
      ? this.props.estadoProceso.estadoMembresia
      : EstadoProcesoCierre.MEMBRESIA_DEFINICION_METAS;

    return (
      <Container className={s.procesoComisionesContainer}>
        {/* {loading && (
          <div className={s.loadingOverlay}>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
            <p className="mt-2">Procesando...</p>
          </div>
        )} */}

        <div className="alert alert-info">
          <strong>Periodo:</strong> {this.formatPeriodo(this.state.periodo)}
          <span>
            {" "}
            - <strong>Estado actual:</strong>{" "}
            {EstadoProcesoLabels[estadoMembresia]}
          </span>
        </div>

        <div className={s.procesoAccordion}>
          {/* Paso 1: Definición de Metas */}
          <Card className="mb-2">
            {this.renderStepHeader(
              1,
              "Definición de Metas",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_DEFINICION_METAS
              )
            )}
            <Collapse isOpen={openStep === 1}>
              <CardBody>
                {(() => {
                  const validation = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_DEFINICION_METAS
                  );

                  if (!validation.allowed) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validation.reason}
                      </div>
                    );
                  }

                  return (
                    <div>
                      <p className="mb-3">
                        Configure las metas de venta para el periodo. Las metas
                        son reutilizables para periodos posteriores.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() =>
                          window.open(
                            "/comisiones-membresia/configuracion-de-metas",
                            "_blank"
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                        Configurar Metas
                      </button>
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>

          {/* Paso 2: Configuración de Plantilla de Asesores */}
          <Card className="mb-2">
            {this.renderStepHeader(
              2,
              "Configuración de Plantilla de Asesores",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_CONFIGURACION_PLANTILLA
              )
            )}
            <Collapse isOpen={openStep === 2}>
              <CardBody>
                {(() => {
                  const validation = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_CONFIGURACION_PLANTILLA
                  );

                  if (!validation.allowed) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validation.reason}
                      </div>
                    );
                  }

                  return (
                    <div>
                      <p className="mb-3">
                        Configure la plantilla de asesores que participarán en
                        el proceso de comisiones del periodo.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() =>
                          window.open(
                            "/comisiones-membresia/configuracion-de-plantilla",
                            "_blank"
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faUsersCog} className="mr-2" />
                        Configurar Plantilla
                      </button>
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>

          {/* Paso 3: Cálculo de Alcances Porcentuales */}
          <Card className="mb-2">
            {this.renderStepHeader(
              3,
              "Cálculo de Alcances Porcentuales",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES
              )
            )}
            <Collapse isOpen={openStep === 3}>
              <CardBody>
                {(() => {
                  const { estadoProceso } = this.props;
                  const validationCalculo = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES
                  );
                  const validationConfirmacion = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES
                  );
                  const estadoMembresia =
                    estadoProceso && estadoProceso.estadoMembresia;
                  const isExecuting =
                    estadoProceso && estadoProceso.calculoAlcancesEnEjecucion;

                  if (
                    !validationCalculo.allowed &&
                    !validationConfirmacion.allowed &&
                    menorQue(
                      estadoMembresia,
                      EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES
                    )
                  ) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validationCalculo.reason}
                      </div>
                    );
                  }

                  if (isExecuting) {
                    return (
                      <div className="alert alert-info">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Calculando porcentajes de alcance...
                      </div>
                    );
                  }

                  const alcancesConfirmados = !menorQue(
                    estadoMembresia,
                    EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES
                  );

                  const alcancesDescargables = mayorIgualQue(
                    estadoMembresia,
                    EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES
                  );

                  return (
                    <div>
                      {alcancesConfirmados && (
                        <>
                          <div className="alert alert-success mb-3">
                            <FontAwesomeIcon
                              icon={faCheckDouble}
                              className="mr-2"
                            />
                            Los alcances ya fueron confirmados
                          </div>
                          {estadoMembresia ===
                            EstadoProcesoCierre.MEMBRESIA_CONFIRMACION_ALCANCES && (
                            <button
                              type="button"
                              className="btn btn-danger mr-2"
                              onClick={() => {
                                this.openConfirmModal(
                                  "Revertir Confirmación de Alcances",
                                  "¿Está seguro de que desea revertir la confirmación de alcances?",
                                  async () => {
                                    await this.props.deleteConfirmarAlcances(
                                      this.state.periodo
                                    );
                                    await this.props.getEstadoProceso(
                                      this.state.periodo
                                    );
                                    successNotification(
                                      "Confirmación de alcances revertida exitosamente"
                                    );
                                  }
                                );
                              }}
                              disabled={this.state.loading}
                            >
                              <FontAwesomeIcon icon={faUndo} className="mr-2" />
                              Revertir
                            </button>
                          )}
                        </>
                      )}
                      {!alcancesConfirmados && (
                        <>
                          <p className="mb-3">
                            Calcule los porcentajes de alcance de metas para los
                            asesores del periodo.
                          </p>
                          {validationCalculo.allowed && (
                            <button
                              type="button"
                              className="btn btn-primary mr-2"
                              onClick={() => {
                                this.openConfirmModal(
                                  "Calcular Alcances",
                                  "¿Está seguro de que desea calcular los alcances porcentuales para el periodo seleccionado?",
                                  async () => {
                                    await this.props.postCalcularAlcances(
                                      this.state.periodo
                                    );
                                    this.props.getEstadoProceso(
                                      this.state.periodo
                                    );
                                  }
                                );
                              }}
                              disabled={this.state.loading}
                            >
                              <FontAwesomeIcon
                                icon={faCalculator}
                                className="mr-2"
                              />
                              Calcular Alcances
                            </button>
                          )}
                          {estadoMembresia ===
                            EstadoProcesoCierre.MEMBRESIA_CALCULO_ALCANCES && (
                            <>
                              <button
                                type="button"
                                className="btn btn-success mr-2"
                                onClick={() => {
                                  this.openConfirmModal(
                                    "Confirmar Alcances",
                                    "¿Está seguro de que desea confirmar los alcances calculados? \n Esta acción no se puede deshacer.",
                                    async () => {
                                      await this.props.postConfirmarAlcances(
                                        this.state.periodo
                                      );
                                      await this.props.getEstadoProceso(
                                        this.state.periodo
                                      );
                                      this.toggleStep(4, true);
                                    }
                                  );
                                }}
                                disabled={this.state.loading}
                              >
                                <FontAwesomeIcon
                                  icon={faCheckDouble}
                                  className="mr-2"
                                />
                                Confirmar Alcances
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger mr-2"
                                onClick={() => {
                                  this.openConfirmModal(
                                    "Revertir Cálculo de Alcances",
                                    "¿Está seguro de que desea revertir el cálculo de alcances? Esta acción eliminará los datos calculados.",
                                    async () => {
                                      await this.props.deleteCalcularAlcances(
                                        this.state.periodo
                                      );
                                      await this.props.getEstadoProceso(
                                        this.state.periodo
                                      );
                                      successNotification(
                                        "Cálculo de alcances revertido exitosamente"
                                      );
                                    }
                                  );
                                }}
                                disabled={this.state.loading}
                              >
                                <FontAwesomeIcon
                                  icon={faUndo}
                                  className="mr-2"
                                />
                                Revertir
                              </button>
                            </>
                          )}
                        </>
                      )}
                      {alcancesDescargables && (
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={() => {
                            this.setState({ downloading: true });
                            downloadReporteAlcances(
                              this.state.periodo,
                              error => {
                                this.setState({ downloading: false });
                                if (!error) {
                                  successNotification(
                                    "Reporte de alcances descargado"
                                  );
                                }
                              }
                            );
                          }}
                          disabled={this.state.downloading}
                        >
                          <FontAwesomeIcon
                            icon={faFileExcel}
                            className="mr-2"
                          />
                          Descargar Reporte
                        </button>
                      )}
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>

          {/* Paso 4: Precálculo de Reglas de Pago */}
          <Card className="mb-2">
            {this.renderStepHeader(
              4,
              "Precálculo de Reglas de Pago",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL
              )
            )}
            <Collapse isOpen={openStep === 4}>
              <CardBody>
                {(() => {
                  const { estadoProceso } = this.props;
                  const validation = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL
                  );
                  const estadoMembresia =
                    estadoProceso && estadoProceso.estadoMembresia;
                  const isExecuting =
                    estadoProceso && estadoProceso.calculoComisionesEnEjecucion;

                  if (
                    !validation.allowed &&
                    menorQue(
                      estadoMembresia,
                      EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL
                    )
                  ) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validation.reason}
                      </div>
                    );
                  }

                  if (isExecuting) {
                    return (
                      <div className="alert alert-info">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Precálculo de comisiones en segundo plano...
                      </div>
                    );
                  }

                  const precalculoCompletado = !menorQue(
                    estadoMembresia,
                    EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL
                  );

                  return (
                    <div>
                      {precalculoCompletado && (
                        <>
                          <div className="alert alert-success mb-3">
                            <FontAwesomeIcon
                              icon={faCheckDouble}
                              className="mr-2"
                            />
                            El precálculo mensual ya fue completado
                          </div>
                          {estadoMembresia ===
                            EstadoProcesoCierre.MEMBRESIA_PRECALCULO_MENSUAL && (
                            <button
                              type="button"
                              className="btn btn-danger mr-2"
                              onClick={() => {
                                this.openConfirmModal(
                                  "Revertir Precálculo Mensual",
                                  "¿Está seguro de que desea revertir el precálculo mensual? Esta acción eliminará los datos del precálculo.",
                                  async () => {
                                    await this.props.deletePrecalculo(
                                      this.state.periodo,
                                      "MENSUAL"
                                    );
                                    await this.props.getEstadoProceso(
                                      this.state.periodo
                                    );
                                    successNotification(
                                      "Precálculo mensual revertido exitosamente"
                                    );
                                  }
                                );
                              }}
                              disabled={this.state.loading}
                            >
                              <FontAwesomeIcon icon={faUndo} className="mr-2" />
                              Revertir
                            </button>
                          )}
                        </>
                      )}
                      {!precalculoCompletado && (
                        <>
                          <p className="mb-3">
                            Ejecute el precálculo de reglas de pago mensuales
                            del periodo.
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary mr-2"
                            onClick={() => {
                              this.openConfirmModal(
                                "Precálculo Mensual",
                                "¿Está seguro de que desea ejecutar el precálculo de reglas de pago mensuales?",
                                async () => {
                                  await this.props.postPrecalculo(
                                    this.state.periodo,
                                    "MENSUAL"
                                  );
                                  this.props.getEstadoProceso(
                                    this.state.periodo
                                  );
                                }
                              );
                            }}
                            disabled={this.state.loading}
                          >
                            <FontAwesomeIcon
                              icon={faChartBar}
                              className="mr-2"
                            />
                            Ejecutar Precálculo Mensual
                          </button>
                        </>
                      )}
                      {precalculoCompletado && (
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={() => {
                            this.setState({ downloading: true });
                            downloadReportePrecalculo(
                              this.state.periodo,
                              "MENSUAL",
                              error => {
                                this.setState({ downloading: false });
                                if (!error) {
                                  successNotification(
                                    "Reporte de precálculo descargado"
                                  );
                                }
                              }
                            );
                          }}
                          disabled={this.state.downloading}
                        >
                          <FontAwesomeIcon
                            icon={faFileExcel}
                            className="mr-2"
                          />
                          Descargar Reporte
                        </button>
                      )}
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>

          {/* Paso 5: Generación de Comisiones del Periodo */}
          <Card className="mb-2">
            {this.renderStepHeader(
              5,
              "Generación de Comisiones del Periodo",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL
              )
            )}
            <Collapse isOpen={openStep === 5}>
              <CardBody>
                {(() => {
                  const { estadoProceso } = this.props;
                  const validation = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL
                  );
                  const estadoMembresia =
                    estadoProceso && estadoProceso.estadoMembresia;
                  const isExecuting =
                    estadoProceso && estadoProceso.calculoComisionesEnEjecucion;

                  if (
                    !validation.allowed &&
                    menorQue(
                      estadoMembresia,
                      EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL
                    )
                  ) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validation.reason}
                      </div>
                    );
                  }

                  if (isExecuting) {
                    return (
                      <div className="alert alert-info">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Cálculo de comisiones en segundo plano...
                      </div>
                    );
                  }

                  const comisionesCompletadas = !menorQue(
                    estadoMembresia,
                    EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL
                  );
                  const fechaPagoMensual = new Date(this.state.periodo);
                  fechaPagoMensual.setUTCMonth(
                    fechaPagoMensual.getUTCMonth() + 1
                  );
                  fechaPagoMensual.setUTCDate(15);
                  const fechaPagoStr = fechaPagoMensual
                    .toISOString()
                    .split("T")[0];

                  return (
                    <div>
                      {comisionesCompletadas && (
                        <>
                          <div className="alert alert-success mb-3">
                            <FontAwesomeIcon
                              icon={faCheckDouble}
                              className="mr-2"
                            />
                            Las comisiones mensuales ya fueron calculadas
                          </div>
                          {estadoMembresia ===
                            EstadoProcesoCierre.MEMBRESIA_CALCULO_MENSUAL && (
                            <button
                              type="button"
                              className="btn btn-danger mr-2"
                              onClick={() => {
                                this.openConfirmModal(
                                  "Revertir Cálculo de Comisiones Mensuales",
                                  "¿Está seguro de que desea revertir el cálculo de comisiones mensuales? Esta acción eliminará las comisiones generadas.",
                                  async () => {
                                    await this.props.deleteCalcularComisiones(
                                      this.state.periodo,
                                      "MENSUAL"
                                    );
                                    await this.props.getEstadoProceso(
                                      this.state.periodo
                                    );
                                    successNotification(
                                      "Cálculo de comisiones mensuales revertido exitosamente"
                                    );
                                  }
                                );
                              }}
                              disabled={this.state.loading}
                            >
                              <FontAwesomeIcon icon={faUndo} className="mr-2" />
                              Revertir
                            </button>
                          )}
                        </>
                      )}
                      {!comisionesCompletadas && (
                        <>
                          <p className="mb-3">
                            Genere las comisiones mensuales del periodo para los
                            asesores.
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary mr-2"
                            onClick={() => {
                              this.openConfirmModal(
                                "Calcular Comisiones Mensuales",
                                "¿Está seguro de que desea calcular las comisiones mensuales del periodo?",
                                async () => {
                                  await this.props.postCalcularComisiones(
                                    this.state.periodo,
                                    "MENSUAL"
                                  );
                                  this.props.getEstadoProceso(
                                    this.state.periodo
                                  );
                                }
                              );
                            }}
                            disabled={this.state.loading}
                          >
                            <FontAwesomeIcon
                              icon={faCalculator}
                              className="mr-2"
                            />
                            Calcular Comisiones Mensuales
                          </button>
                        </>
                      )}
                      {comisionesCompletadas && (
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={() => {
                            this.setState({ downloading: true });
                            downloadReporteComisiones(fechaPagoStr, error => {
                              this.setState({ downloading: false });
                              if (!error) {
                                successNotification(
                                  "Reporte de comisiones descargado"
                                );
                              }
                            });
                          }}
                          disabled={this.state.downloading}
                        >
                          <FontAwesomeIcon
                            icon={faFileExcel}
                            className="mr-2"
                          />
                          Descargar Reporte
                        </button>
                      )}
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>

          {/* Paso 6: Reproceso de Precálculo Quincenal */}
          <Card className="mb-2">
            {this.renderStepHeader(
              6,
              "Reproceso de Precálculo Quincenal",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL
              )
            )}
            <Collapse isOpen={openStep === 6}>
              <CardBody>
                {(() => {
                  const { estadoProceso } = this.props;
                  const validation = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL
                  );
                  const estadoMembresia =
                    estadoProceso && estadoProceso.estadoMembresia;
                  const isExecuting =
                    estadoProceso && estadoProceso.calculoComisionesEnEjecucion;

                  if (
                    !validation.allowed &&
                    menorQue(
                      estadoMembresia,
                      EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL
                    )
                  ) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validation.reason}
                      </div>
                    );
                  }

                  if (isExecuting) {
                    return (
                      <div className="alert alert-info">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Precálculo de comisiones en segundo plano...
                      </div>
                    );
                  }

                  const precalculoCompletado = !menorQue(
                    estadoMembresia,
                    EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL
                  );

                  return (
                    <div>
                      {precalculoCompletado && (
                        <>
                          <div className="alert alert-success mb-3">
                            <FontAwesomeIcon
                              icon={faCheckDouble}
                              className="mr-2"
                            />
                            El precálculo postquincenal ya fue completado
                          </div>
                          {estadoMembresia ===
                            EstadoProcesoCierre.MEMBRESIA_PRECALCULO_POSTQUINCENAL && (
                            <button
                              type="button"
                              className="btn btn-danger mr-2"
                              onClick={() => {
                                this.openConfirmModal(
                                  "Revertir Precálculo Postquincenal",
                                  "¿Está seguro de que desea revertir el precálculo postquincenal? Esta acción eliminará los datos del precálculo.",
                                  async () => {
                                    await this.props.deletePrecalculo(
                                      this.state.periodo,
                                      "QUINCENAL"
                                    );
                                    await this.props.getEstadoProceso(
                                      this.state.periodo
                                    );
                                    successNotification(
                                      "Precálculo postquincenal revertido exitosamente"
                                    );
                                  }
                                );
                              }}
                              disabled={this.state.loading}
                            >
                              <FontAwesomeIcon icon={faUndo} className="mr-2" />
                              Revertir
                            </button>
                          )}
                        </>
                      )}
                      {!precalculoCompletado && (
                        <>
                          <p className="mb-3">
                            Ejecute el precálculo postquincenal de comisiones
                            para validar ajustes.
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary mr-2"
                            onClick={() => {
                              this.openConfirmModal(
                                "Precálculo Postquincenal",
                                "¿Está seguro de que desea ejecutar el precálculo de reglas de pago postquincenales?",
                                async () => {
                                  await this.props.postPrecalculo(
                                    this.state.periodo,
                                    "QUINCENAL"
                                  );
                                  this.props.getEstadoProceso(
                                    this.state.periodo
                                  );
                                }
                              );
                            }}
                            disabled={this.state.loading}
                          >
                            <FontAwesomeIcon
                              icon={faChartBar}
                              className="mr-2"
                            />
                            Ejecutar Precálculo Postquincenal
                          </button>
                        </>
                      )}
                      {precalculoCompletado && (
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={() => {
                            this.setState({ downloading: true });
                            downloadReportePrecalculo(
                              this.state.periodo,
                              "QUINCENAL",
                              error => {
                                this.setState({ downloading: false });
                                if (!error) {
                                  successNotification(
                                    "Reporte de precálculo descargado"
                                  );
                                }
                              }
                            );
                          }}
                          disabled={this.state.downloading}
                        >
                          <FontAwesomeIcon
                            icon={faFileExcel}
                            className="mr-2"
                          />
                          Descargar Reporte
                        </button>
                      )}
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>

          {/* Paso 7: Reproceso de Comisiones Quincenal */}
          <Card className="mb-2">
            {this.renderStepHeader(
              7,
              "Reproceso de Comisiones Quincenal",
              mayorIgualQue(
                estadoMembresia,
                EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL
              )
            )}
            <Collapse isOpen={openStep === 7}>
              <CardBody>
                {(() => {
                  const { estadoProceso } = this.props;
                  const validation = this.isActivityAllowed(
                    EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL
                  );
                  const estadoMembresia =
                    estadoProceso && estadoProceso.estadoMembresia;
                  const isExecuting =
                    estadoProceso && estadoProceso.calculoComisionesEnEjecucion;

                  if (
                    !validation.allowed &&
                    menorQue(
                      estadoMembresia,
                      EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL
                    )
                  ) {
                    return (
                      <div className="alert alert-warning">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="mr-2"
                        />
                        {validation.reason}
                      </div>
                    );
                  }

                  if (isExecuting) {
                    return (
                      <div className="alert alert-info">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Cálculo de comisiones en segundo plano...
                      </div>
                    );
                  }

                  const comisionesCompletadas = !menorQue(
                    estadoMembresia,
                    EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL
                  );
                  const fechaPagoQuincenal = new Date(this.state.periodo);
                  fechaPagoQuincenal.setUTCMonth(
                    fechaPagoQuincenal.getUTCMonth() + 2
                  );
                  fechaPagoQuincenal.setUTCDate(0);
                  const fechaPagoQuincenalStr = fechaPagoQuincenal
                    .toISOString()
                    .split("T")[0];

                  return (
                    <div>
                      {comisionesCompletadas && (
                        <>
                          <div className="alert alert-success mb-3">
                            <FontAwesomeIcon
                              icon={faCheckDouble}
                              className="mr-2"
                            />
                            Las comisiones postquincenales ya fueron calculadas
                          </div>
                          {estadoMembresia ===
                            EstadoProcesoCierre.MEMBRESIA_CALCULO_POSTQUINCENAL && (
                            <button
                              type="button"
                              className="btn btn-danger mr-2"
                              onClick={() => {
                                this.openConfirmModal(
                                  "Revertir Cálculo de Comisiones Postquincenales",
                                  "¿Está seguro de que desea revertir el cálculo de comisiones postquincenales? Esta acción eliminará las comisiones generadas.",
                                  async () => {
                                    await this.props.deleteCalcularComisiones(
                                      this.state.periodo,
                                      "QUINCENAL"
                                    );
                                    await this.props.getEstadoProceso(
                                      this.state.periodo
                                    );
                                    successNotification(
                                      "Cálculo de comisiones postquincenales revertido exitosamente"
                                    );
                                  }
                                );
                              }}
                              disabled={this.state.loading}
                            >
                              <FontAwesomeIcon icon={faUndo} className="mr-2" />
                              Revertir
                            </button>
                          )}
                        </>
                      )}
                      {!comisionesCompletadas && (
                        <>
                          <p className="mb-3">
                            Genere las comisiones postquincenales del periodo
                            para los asesores.
                          </p>
                          <button
                            type="button"
                            className="btn btn-primary mr-2"
                            onClick={() => {
                              this.openConfirmModal(
                                "Calcular Comisiones Postquincenales",
                                "¿Está seguro de que desea calcular las comisiones postquincenales del periodo?",
                                async () => {
                                  await this.props.postCalcularComisiones(
                                    this.state.periodo,
                                    "QUINCENAL"
                                  );
                                  this.props.getEstadoProceso(
                                    this.state.periodo
                                  );
                                }
                              );
                            }}
                            disabled={this.state.loading}
                          >
                            <FontAwesomeIcon
                              icon={faCalculator}
                              className="mr-2"
                            />
                            Calcular Comisiones Postquincenales
                          </button>
                        </>
                      )}
                      {comisionesCompletadas && (
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={() => {
                            this.setState({ downloading: true });
                            downloadReporteComisiones(
                              fechaPagoQuincenalStr,
                              error => {
                                this.setState({ downloading: false });
                                if (!error) {
                                  successNotification(
                                    "Reporte de comisiones descargado"
                                  );
                                }
                              }
                            );
                          }}
                          disabled={this.state.downloading}
                        >
                          <FontAwesomeIcon
                            icon={faFileExcel}
                            className="mr-2"
                          />
                          Descargar Reporte
                        </button>
                      )}
                    </div>
                  );
                })()}
              </CardBody>
            </Collapse>
          </Card>
        </div>

        <ConfirmModal
          isOpen={this.state.confirmModal.isOpen}
          title={this.state.confirmModal.title}
          message={this.state.confirmModal.message}
          requireInput={false}
          inputType=""
          inputTitle=""
          waiting={this.state.loading}
          options={[]}
          size="md"
          currentValue={null}
          okTitle="Confirmar"
          cancelTitle="Cancelar"
          ok={this.handleConfirmAction}
          cancel={this.closeConfirmModal}
        />
      </Container>
    );
  }
}
